import { gsap } from 'gsap';
import { arrayRandom } from '../../../lib/util/jsTools';
import {
  BasicBlockEntity,
  BlockEntity,
  despawnBlockEntity,
  MushroomBlockEntity,
  spawnBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { blockIterateNeighbors } from '../../util/blockTools';
import { IEffect } from './IEffect';
import { BasicBlockProps, blockColorMap } from '../../defs/block';
import { BreakEffect } from './BreakEffect';
import { app } from '../../app';

// types
//-----------------------------------------------------------------------------
// public
export type MushroomEffectOptions = {
  mushrooms: MushroomBlockEntity[];
};

// private
type MushroomBlockPair = {
  mushroom: MushroomBlockEntity;
  basic: BasicBlockEntity;
};

/*
    spawns one new mushroom from the position of a random existing mushroom on top of
    a random neighboring basic block (if any exist)
*/
export class MushroomEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: MushroomEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: MushroomEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  public static assets(): string[] {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    // find mushroom+basic pairs
    const pairs = this._findPairs();

    if (pairs.length > 0) {
      const phase = this._scene.sessionEntity.c.phase;

      // pick a random one
      const pair = arrayRandom(pairs);

      if (pair) {
        // push activations
        phase.activePush();

        // do mushroom action from the mushroom block to its neighbor block
        await this._spawnMushroom(pair.basic);

        // pop activations
        await phase.activePop();
      }
    }
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _spawnMushroom(target: BasicBlockEntity) {
    const targetView = target.c.blockBasic.view;
    const baseX = targetView.x;
    const offset = 3;
    const animator = { tick: 0 };
    const shakeDuration = 0.4;
    const shakeFactor = 15 * (2 / shakeDuration);

    await gsap.to(animator, {
      duration: shakeDuration,
      tick: shakeDuration,
      ease: 'quad.in',
      onUpdate: () => {
        targetView.x = baseX + Math.sin(animator.tick * shakeFactor) * offset;
      },
    });

    // despawn occupied target
    despawnBlockEntity(this._scene, target, true);

    // play spawn SFX
    app.sound.play('button-exit');

    // play block break effect
    const position = target.c.position.mapPosition;
    const colorId = (target.c.block.props as BasicBlockProps).color;
    const color = blockColorMap[colorId].base;

    void new BreakEffect(this._scene, {
      position,
      size: target.c.block.props,
      color,
    }).execute();

    // spawn mushroom block at position
    const mushroom = spawnBlockEntity(
      this._scene,
      { id: 'mushroom' },
      position,
    ) as MushroomBlockEntity;

    // trigger ungoal
    this._triggerUngoal(mushroom);

    // animate to requested view position
    void mushroom.c.blockMushroom.view.animateIn();
  }

  // private: support
  //-------------------------------------------------------------------------
  private _triggerUngoal(source: BlockEntity) {
    const id = 'mushroom';
    const map = this._scene.sessionEntity.c.map;
    let count = map.goals[id];

    // if mushroom is a goal
    if (count && count > 0) {
      // increment goal count
      map.goals[id] = ++count;

      // notify ungoal
      this._scene.events.publish({ id: 'ungoal', goalId: 'mushroom', source });
    }
  }

  private _findPairs(): MushroomBlockPair[] {
    // from each given mushroom create list of pairs of that mushroom and a neighboring basic block
    return this._options.mushrooms.reduce((pairs, mushroom) => {
      // iterate mushroom's immediate neighbors
      blockIterateNeighbors(
        this._scene.sessionEntity.c.map,
        mushroom,
        (neighbor, overlay) => {
          // skip subject
          if (neighbor === mushroom) return true;
          // if neighbor is un-overlayed basic block create pair and end iteration
          if (neighbor.c.block.props.type === 'basic' && !overlay) {
            pairs.push({
              mushroom,
              basic: neighbor as BasicBlockEntity,
            });
          }
          return false;
        },
      );
      return pairs;
    }, [] as MushroomBlockPair[]);
  }
}
